// ./components/SelectGLAccountModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Flex,
  Typography,
  message,
  Spin,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";

const { Title } = Typography;

const SelectGLAccountModal = ({
  visible,
  onClose,
  selectedRow,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [glAccountOptions, setGlAccountOptions] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedGlAcct, setSelectedGlAcct] = useState(null);
  const [loading, setLoading] = useState(false);

  const layout = {
    labelCol: {
      span: 6,
    },
    wrapperCol: {
      span: 16,
    },
  };

  useEffect(() => {
    if (visible && selectedRow) {
      fetchGLAccounts();
      form.setFieldsValue({
        requestNo: selectedRow.requestNo,
        glAcct: selectedRow.glAcct || undefined,
        _record: selectedRow,
      });
      setSelectedGlAcct(selectedRow.glAcct || null);
    }
  }, [visible, selectedRow]);

  const fetchGLAccounts = async () => {
    setLoading(true);
    try {
      const response = await http.get(api.getGLAccountList);
      if (response?.data) {
        const options = response.data.map((item) => ({
          label: item.glAcctTitle,
          value: item.glAcct,
        }));
        setGlAccountOptions(options);
      }
    } catch (error) {
      message.error("Failed to fetch GL Account list");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGlSelect = (value) => {
    setSelectedGlAcct(value);
  };

  const handleCancel = () => {
    setSelectedGlAcct(null);
    form.resetFields();
    onClose();
  };

  const handleGlSave = async () => {
    if (!selectedRow || !selectedGlAcct) {
      message.warning("Please select a GL Account.");
      return;
    }
    setConfirmLoading(true);
    try {
      const response = await http.get(api.getAddGlAcctToInfo, {
        params: {
          id: selectedRow.id,
          glAcct: selectedGlAcct,
        },
      });
      if (response.code === "1") {
        message.success("GL Account added successfully.");
        onSuccess?.();
        handleCancel();
      } else {
        commonFn.modalFailedFn(response);
      }
    } catch (error) {
      message.error("Request failed.");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Flex justify="flex-start" align="flex-start" className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Title level={4} className="mg">
            Select GL Account
          </Title>
        </Flex>
      }
      open={visible}
      onCancel={handleCancel}
      width="50%"
      destroyOnClose
      confirmLoading={confirmLoading}
      styles={{
        body: {
          height: "30vh",
          padding: "16px 24px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      maskClosable={false}
      footer={[
        <div
          key="footer"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={confirmLoading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>,
      ]}
    >
      <Spin spinning={loading} tip="Loading GL Accounts...">
        <Flex justify="space-between" align="flex-start">
          <div className="model_top" style={{ width: "100%" }}>
            <div className="model_content" style={{ marginTop: 20 }}>
              <Form
                {...layout}
                name="glForm"
                form={form}
                onFinish={handleGlSave}
                labelCol={{ style: { width: "150px" } }}
                wrapperCol={{ style: { flex: 1 } }}
              >
                <Form.Item name="requestNo" label="Request No">
                  <Input
                    disabled
                    value={selectedRow?.requestNo}
                    placeholder="Request No will appear here"
                  />
                </Form.Item>

                <Form.Item name="glAcct" label="GL Acct">
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Please select a GL Account"
                    options={glAccountOptions}
                    onChange={handleGlSelect}
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="_record" noStyle>
                  <Input type="hidden" />
                </Form.Item>
              </Form>
            </div>
          </div>
        </Flex>
      </Spin>
    </Modal>
  );
};

export default SelectGLAccountModal;